import React, { useState, useMemo, useEffect } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Select, MenuItem, FormControl, InputLabel, IconButton, Grid, Button, SelectChangeEvent, Tooltip, Dialog, DialogContent, DialogTitle, InputAdornment, Chip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FormattedMessage, useIntl } from 'react-intl';
import { Expense } from '../types/Expense';
import {
  Edit,
  Delete,
  Sort,
  ArrowDropUp,
  ArrowDropDown,
  SwapHoriz,
  Label,
  Search,
  CalendarToday,
  Euro,
  Person,
  Save,
  Cancel,
  CloudUpload,
  FileDownload,
  Visibility,
  Receipt
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import CustomChip from './CustomChip';
import { designTokens } from '../theme/designTokens';
import { getCategoryIcon } from '../utils/categoryIcons';
import { identifyBrand, getLogoUrl } from '../utils/brandUtils';

interface FilteredExpensesProps {
  expenses: Expense[];
  filter: {
    year: number;
    month: number | 'all';
    payer: string;
  };
  payers: string[];
  categories: string[];
  onEditExpense: (expense: Expense, file?: File) => Promise<void>;
  onDeleteExpense: (id: string) => void;
  payerColors: { [key: string]: string };
  categoryIcons?: { [key: string]: string };
  showYearFilter?: boolean;
}

type SortableExpenseKey = keyof Omit<Expense, 'id' | 'fileUrl'> | 'type' | 'sharedWithOrDebtFrom';

interface Column {
  id: SortableExpenseKey | 'actions' | 'receipt';
  label: React.ReactNode;
  render: (expense: Expense, isEditing: boolean, editingExpense: Expense | null, handleEditChange: any, handleSharedChange: any) => React.ReactNode;
}

type SortConfig = {
  key: SortableExpenseKey;
  direction: 'asc' | 'desc';
} | null;

const FilteredExpenses: React.FC<FilteredExpensesProps> = ({
  expenses,
  filter,
  payers,
  categories,
  onEditExpense,
  onDeleteExpense,
  payerColors,
  categoryIcons = {},
  showYearFilter = true
}) => {
  const intl = useIntl();
  const [selectedPayer, setSelectedPayer] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [internalYear, setInternalYear] = useState<number>(filter.year);
  const [descriptionFilter, setDescriptionFilter] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'date', direction: 'desc' });

  const [openReceiptDialog, setOpenReceiptDialog] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState('');

  const [editingFile, setEditingFile] = useState<File | null>(null);

  useEffect(() => {
    setInternalYear(filter.year);
  }, [filter.year]);

  const filteredExpenses = useMemo(() => {
    return expenses.filter(expense => {
      const dateMatch = new Date(expense.date).getFullYear() === internalYear &&
        (filter.month === 'all' || new Date(expense.date).getMonth() === filter.month);
      const descriptionMatch = expense.description.toLowerCase().includes(descriptionFilter.toLowerCase());
      const payerMatch = filter.payer === '' || expense.payer === filter.payer;
      const categoryMatch = selectedCategory === '' || expense.category === selectedCategory;
      return dateMatch && descriptionMatch && payerMatch && categoryMatch;
    });
  }, [expenses, filter, internalYear, descriptionFilter, selectedCategory]);

  const totalExpenses = useMemo(() => {
    return filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0);
  }, [filteredExpenses]);

  const handleEdit = (expense: Expense) => {
    setEditingExpense({ ...expense });
    setEditingFile(null);
  };

  const handleSaveEdit = async () => {
    if (editingExpense) {
      try {
        await onEditExpense(editingExpense, editingFile || undefined);
        setEditingExpense(null);
        setEditingFile(null);
      } catch (error) {
        console.error("Error saving edited expense:", error);
        // Você pode adicionar uma notificação de erro aqui
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingExpense(null);
  };

  const handleDelete = (id: string) => {
    if (window.confirm(intl.formatMessage({ id: 'delete' }))) {
      onDeleteExpense(id);
    }
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>) => {
    if (editingExpense) {
      const { name, value } = e.target;
      setEditingExpense((prev: Expense | null) => {
        if (!prev) return null;
        return {
          ...prev,
          [name]: name === 'amount' ? Number(value) : value,
        };
      });
    }
  };

  const handleSharedChange = (e: SelectChangeEvent<string>) => {
    if (editingExpense) {
      setEditingExpense((prev: Expense | null) => {
        if (!prev) return null;
        return {
          ...prev,
          shared: e.target.value === 'yes',
        };
      });
    }
  };

  const sortedExpenses = useMemo(() => {
    let sortableExpenses = [...filteredExpenses];
    if (sortConfig !== null) {
      sortableExpenses.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        if (sortConfig.key === 'type') {
          aValue = a.isDebt ? 'debt' : a.shared ? 'shared' : '-';
          bValue = b.isDebt ? 'debt' : b.shared ? 'shared' : '-';
        } else if (sortConfig.key === 'sharedWithOrDebtFrom') {
          aValue = a.shared ? a.sharedWith : a.isDebt ? a.debtFrom : '-';
          bValue = b.shared ? b.sharedWith : b.isDebt ? b.debtFrom : '-';
        } else {
          aValue = a[sortConfig.key as keyof Expense];
          bValue = b[sortConfig.key as keyof Expense];
        }

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
        }
        if (typeof bValue === 'string') {
          bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableExpenses;
  }, [filteredExpenses, sortConfig]);

  const requestSort = (key: SortableExpenseKey) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const [columns, setColumns] = useState<Column[]>([]);

  const handleOpenReceipt = (fileUrl: string) => {
    setSelectedReceipt(fileUrl);
    setOpenReceiptDialog(true);
  };

  const handleCloseReceipt = () => {
    setOpenReceiptDialog(false);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setEditingFile(event.target.files[0]);
    }
  };

  const exportToCSV = () => {
    const headers = ['Categoria', 'Descrição', 'Data', 'Montante', 'Pagador', 'Tipo', 'Partilhada Com / Dívida De'];
    const csvContent = [
      '\uFEFF' + headers.join(';'), // BOM for UTF-8
      ...sortedExpenses.map(expense => [
        `"${expense.category.replace(/"/g, '""')}"`,
        `"${expense.description.replace(/"/g, '""')}"`,
        new Date(expense.date).toLocaleDateString(),
        `"€${expense.amount.toFixed(2)}"`,
        `"${expense.payer.replace(/"/g, '""')}"`,
        `"${expense.isDebt ? 'Dívida' : expense.shared ? 'Partilhada' : '-'}"`,
        `"${(expense.shared && expense.sharedWith ? expense.sharedWith : expense.isDebt && expense.debtFrom ? expense.debtFrom : '-').replace(/"/g, '""')}"`
      ].join(';'))
    ].join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'despesas_filtradas.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Ordena as categorias alfabeticamente
  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => a.localeCompare(b));
  }, [categories]);

  useEffect(() => {
    setColumns([
      {
        id: 'category',
        label: (
          <Box display="flex" alignItems="center" gap={1}>
            <Label sx={{ fontSize: 18, color: designTokens.colors.text.secondary }} />
            <FormattedMessage id="category" />
          </Box>
        ),
        render: (expense, isEditing, editingExpense, handleEditChange) => (
          isEditing ? (
            <Select
              value={editingExpense?.category || ''}
              onChange={handleEditChange}
              name="category"
              fullWidth
              size="small"
              sx={{ borderRadius: designTokens.borderRadius.md }}
              renderValue={(selected) => (
                <Box display="flex" alignItems="center" gap={1}>
                  {getCategoryIcon(selected as string, categoryIcons)}
                  <Typography>{selected}</Typography>
                </Box>
              )}
            >
              {categories.map(cat => (
                <MenuItem key={cat} value={cat}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {getCategoryIcon(cat, categoryIcons)}
                    <Typography>{cat}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          ) : (
            <Box display="flex" alignItems="center" gap={1}>
              {getCategoryIcon(expense.category, categoryIcons)}
              <Typography variant="body2">{expense.category}</Typography>
            </Box>
          )
        ),
      },
      {
        id: 'description',
        label: (
          <Box display="flex" alignItems="center" gap={1}>
            <Search sx={{ fontSize: 18, color: designTokens.colors.text.secondary }} />
            <FormattedMessage id="description" />
          </Box>
        ),
        render: (expense, isEditing, editingExpense, handleEditChange) => (
          isEditing ? (
            <TextField
              value={editingExpense?.description || ''}
              onChange={handleEditChange}
              name="description"
              fullWidth
              size="small"
              sx={{ '& .MuiInputBase-root': { borderRadius: designTokens.borderRadius.md } }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {(() => {
                const brand = identifyBrand(expense.description);
                if (brand) {
                  return (
                    <Box
                      component="img"
                      src={getLogoUrl(brand.domain)}
                      alt={brand.name}
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: '4px',
                        flexShrink: 0
                      }}
                      onError={(e: any) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  );
                }
                return null;
              })()}
              <Typography variant="body2" sx={{ fontWeight: designTokens.typography.fontWeight.medium }}>
                {expense.description}
              </Typography>
            </Box>
          )
        ),
      },
      {
        id: 'date',
        label: (
          <Box display="flex" alignItems="center" gap={1}>
            <CalendarToday sx={{ fontSize: 18, color: designTokens.colors.text.secondary }} />
            <FormattedMessage id="date" />
          </Box>
        ),
        render: (expense, isEditing, editingExpense, handleEditChange) => (
          isEditing ? (
            <TextField
              type="date"
              value={editingExpense?.date || ''}
              onChange={handleEditChange}
              name="date"
              fullWidth
              size="small"
              sx={{ '& .MuiInputBase-root': { borderRadius: designTokens.borderRadius.md } }}
            />
          ) : (
            <Typography variant="body2">{new Date(expense.date).toLocaleDateString()}</Typography>
          )
        ),
      },
      {
        id: 'amount',
        label: (
          <Box display="flex" alignItems="center" gap={1}>
            <Euro sx={{ fontSize: 18, color: designTokens.colors.text.secondary }} />
            <FormattedMessage id="amount" />
          </Box>
        ),
        render: (expense, isEditing, editingExpense, handleEditChange) => (
          isEditing ? (
            <TextField
              type="number"
              value={editingExpense?.amount || ''}
              onChange={handleEditChange}
              name="amount"
              fullWidth
              size="small"
              sx={{ '& .MuiInputBase-root': { borderRadius: designTokens.borderRadius.md } }}
            />
          ) : (
            <Typography variant="body2" sx={{ fontWeight: designTokens.typography.fontWeight.bold, color: designTokens.colors.primary.main }}>
              €{expense.amount.toFixed(2)}
            </Typography>
          )
        ),
      },
      {
        id: 'payer',
        label: (
          <Box display="flex" alignItems="center" gap={1}>
            <Person sx={{ fontSize: 18, color: designTokens.colors.text.secondary }} />
            <FormattedMessage id="payer" />
          </Box>
        ),
        render: (expense, isEditing, editingExpense, handleEditChange) => (
          isEditing ? (
            <Select
              value={editingExpense?.payer || ''}
              onChange={handleEditChange}
              name="payer"
              fullWidth
              size="small"
              sx={{ borderRadius: designTokens.borderRadius.md }}
            >
              {payers.map(payer => (
                <MenuItem key={payer} value={payer}>{payer}</MenuItem>
              ))}
            </Select>
          ) : (
            <CustomChip
              label={expense.payer}
              color={payerColors[expense.payer] || '#E0E0E0'}
              size="small"
            />
          )
        ),
      },
      {
        id: 'type',
        label: (
          <Box display="flex" alignItems="center" gap={1}>
            <SwapHoriz sx={{ fontSize: 18, color: designTokens.colors.text.secondary }} />
            <FormattedMessage id="type" />
          </Box>
        ),
        render: (expense) => (
          <Tooltip title={expense.shared ? `Shared with ${expense.sharedWith}` : expense.isDebt ? `Debt from ${expense.debtFrom}` : ''}>
            <Chip
              label={intl.formatMessage({ id: expense.isDebt ? 'debt' : expense.shared ? 'shared' : '-' })}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.7rem',
                borderColor: expense.isDebt ? designTokens.colors.error.main : expense.shared ? designTokens.colors.primary.main : 'transparent',
                color: expense.isDebt ? designTokens.colors.error.main : expense.shared ? designTokens.colors.primary.main : designTokens.colors.text.secondary,
                fontWeight: 'bold',
                bgcolor: expense.isDebt ? `${designTokens.colors.error.main}11` : expense.shared ? `${designTokens.colors.primary.main}11` : 'transparent'
              }}
            />
          </Tooltip>
        ),
      },
      {
        id: 'actions',
        label: <FormattedMessage id="actions" />,
        render: (expense) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {editingExpense?.id === expense.id ? (
              <>
                <Tooltip title={intl.formatMessage({ id: 'save' })}>
                  <IconButton
                    onClick={handleSaveEdit}
                    size="small"
                    sx={{
                      color: designTokens.colors.success.main,
                      transition: `all ${designTokens.transitions.fast}`,
                      '&:hover': {
                        backgroundColor: `${designTokens.colors.success.main}15`,
                        transform: 'translateY(-1px)',
                      }
                    }}
                  >
                    <Save fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={intl.formatMessage({ id: 'cancel' })}>
                  <IconButton
                    onClick={handleCancelEdit}
                    size="small"
                    sx={{
                      color: designTokens.colors.error.main,
                      transition: `all ${designTokens.transitions.fast}`,
                      '&:hover': {
                        backgroundColor: `${designTokens.colors.error.main}15`,
                        transform: 'translateY(-1px)',
                      }
                    }}
                  >
                    <Cancel fontSize="small" />
                  </IconButton>
                </Tooltip>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id={`upload-file-${expense.id}`}
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor={`upload-file-${expense.id}`}>
                  <Tooltip title={intl.formatMessage({ id: 'uploadReceipt' })}>
                    <IconButton
                      component="span"
                      size="small"
                      sx={{
                        color: designTokens.colors.info.main,
                        transition: `all ${designTokens.transitions.fast}`,
                        '&:hover': {
                          backgroundColor: `${designTokens.colors.info.main}15`,
                          transform: 'translateY(-1px)',
                        }
                      }}
                    >
                      <CloudUpload fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </label>
                {editingFile && (
                  <Typography variant="caption" sx={{ color: designTokens.colors.text.secondary, ml: 1, alignSelf: 'center' }}>
                    {editingFile.name}
                  </Typography>
                )}
              </>
            ) : (
              <>
                <Tooltip title={intl.formatMessage({ id: 'edit' })}>
                  <IconButton
                    onClick={() => handleEdit(expense)}
                    size="small"
                    sx={{
                      color: designTokens.colors.primary.main,
                      transition: `all ${designTokens.transitions.fast}`,
                      '&:hover': {
                        backgroundColor: `${designTokens.colors.primary.main}15`,
                        transform: 'translateY(-1px)',
                      }
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={intl.formatMessage({ id: 'delete' })}>
                  <IconButton
                    onClick={() => handleDelete(expense.id)}
                    size="small"
                    sx={{
                      color: designTokens.colors.error.main,
                      transition: `all ${designTokens.transitions.fast}`,
                      '&:hover': {
                        backgroundColor: `${designTokens.colors.error.main}15`,
                        transform: 'translateY(-1px)',
                      }
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
                {expense.fileUrl && (
                  <Tooltip title={intl.formatMessage({ id: 'viewReceipt' })}>
                    <IconButton
                      onClick={() => handleOpenReceipt(expense.fileUrl || '')}
                      size="small"
                      sx={{
                        color: designTokens.colors.info.main,
                        transition: `all ${designTokens.transitions.fast}`,
                        '&:hover': {
                          backgroundColor: `${designTokens.colors.info.main}15`,
                          transform: 'translateY(-1px)',
                        }
                      }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        ),
      },
    ]);
  }, [intl, payers, categories, payerColors, editingExpense, editingFile]);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const items = Array.from(columns);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setColumns(items);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box className="filtered-expenses" sx={{ mt: 2 }}>
        <Paper elevation={0} sx={{
          p: designTokens.spacing.lg,
          mb: 4,
          borderRadius: designTokens.borderRadius.lg,
          background: designTokens.colors.background.paper,
          border: designTokens.glassmorphism.border,
          boxShadow: designTokens.shadows.md,
        }}>
          <Grid container spacing={2} alignItems="center">
            {showYearFilter && (
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="year-select-label"><FormattedMessage id="year" /></InputLabel>
                  <Select
                    labelId="year-select-label"
                    value={internalYear}
                    label={intl.formatMessage({ id: "year" })}
                    onChange={(e) => setInternalYear(Number(e.target.value))}
                    sx={{ borderRadius: designTokens.borderRadius.md }}
                  >
                    {Array.from({ length: 7 }, (_, i) => 2024 + i).map(year => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="payer-select-label"><FormattedMessage id="payer" /></InputLabel>
                <Select
                  labelId="payer-select-label"
                  value={selectedPayer}
                  label={intl.formatMessage({ id: "payer" })}
                  onChange={(e) => setSelectedPayer(e.target.value as string)}
                  sx={{ borderRadius: designTokens.borderRadius.md }}
                >
                  <MenuItem value=""><FormattedMessage id="allPayers" /></MenuItem>
                  {payers.map(payer => (
                    <MenuItem key={payer} value={payer}>{payer}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="category-select-label"><FormattedMessage id="category" /></InputLabel>
                <Select
                  labelId="category-select-label"
                  value={selectedCategory}
                  label={intl.formatMessage({ id: "category" })}
                  onChange={(e) => setSelectedCategory(e.target.value as string)}
                  sx={{ borderRadius: designTokens.borderRadius.md }}
                >
                  <MenuItem value=""><FormattedMessage id="allCategories" /></MenuItem>
                  {sortedCategories.map(category => (
                    <MenuItem key={category} value={category}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {getCategoryIcon(category, categoryIcons)}
                        <Typography>{category}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label={intl.formatMessage({ id: "searchDescription" })}
                value={descriptionFilter}
                onChange={(e) => setDescriptionFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 18, color: designTokens.colors.text.secondary }} />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: designTokens.borderRadius.md }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={exportToCSV}
                fullWidth
                sx={{
                  height: '40px',
                  borderRadius: designTokens.borderRadius.md,
                  background: designTokens.colors.primary.gradient,
                  textTransform: 'none',
                  boxShadow: designTokens.shadows.sm,
                  '&:hover': {
                    boxShadow: designTokens.shadows.md,
                    opacity: 0.9
                  }
                }}
              >
                <FormattedMessage id="exportCSV" />
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer sx={{
          maxHeight: 600,
          borderRadius: designTokens.borderRadius.lg,
          background: designTokens.colors.background.paper,
          border: designTokens.glassmorphism.border,
          boxShadow: designTokens.shadows.md,
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-thumb': { background: designTokens.colors.background.elevated, borderRadius: 4 }
        }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.id as string} sx={{
                    fontWeight: designTokens.typography.fontWeight.semibold,
                    background: designTokens.colors.background.paper,
                    color: designTokens.colors.text.secondary,
                    borderBottom: `1px solid ${designTokens.colors.background.elevated}`,
                    py: 2
                  }}>
                    <Box display="flex" alignItems="center">
                      {column.label}
                      {column.id !== 'actions' && (
                        <IconButton size="small" onClick={() => requestSort(column.id as SortableExpenseKey)} sx={{ ml: 0.5 }}>
                          {sortConfig?.key === column.id ? (
                            sortConfig.direction === 'asc' ? <ArrowDropUp sx={{ color: designTokens.colors.primary.main }} /> : <ArrowDropDown sx={{ color: designTokens.colors.primary.main }} />
                          ) : (
                            <Sort sx={{ fontSize: 16, opacity: 0.3 }} />
                          )}
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedExpenses.map(expense => (
                <TableRow
                  key={expense.id}
                  sx={{
                    '&:hover': { background: designTokens.colors.background.default },
                    transition: 'background 0.2s ease'
                  }}
                >
                  {columns.map(column => (
                    <TableCell key={column.id as string} sx={{ py: 1.5, borderBottom: `1px solid ${designTokens.colors.background.elevated}55` }}>
                      {column.render(
                        expense,
                        editingExpense?.id === expense.id,
                        editingExpense,
                        handleEditChange,
                        handleSharedChange
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{
          mt: 3,
          p: 2,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'baseline',
          gap: 1
        }}>
          <Typography variant="body1" sx={{ color: designTokens.colors.text.secondary }}>
            <FormattedMessage id="total" />:
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: designTokens.typography.fontWeight.bold, color: designTokens.colors.primary.main }}>
            €{totalExpenses.toFixed(2)}
          </Typography>
        </Box>

        <Dialog
          open={openReceiptDialog}
          onClose={handleCloseReceipt}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: designTokens.borderRadius.lg }
          }}
        >
          <DialogTitle sx={{ fontWeight: designTokens.typography.fontWeight.bold }}>
            <FormattedMessage id="receipt" />
          </DialogTitle>
          <DialogContent>
            {selectedReceipt && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <img src={selectedReceipt} alt="Receipt" style={{ width: '100%', height: 'auto', borderRadius: designTokens.borderRadius.md }} />
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default FilteredExpenses;